import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CircleDashed,
  DollarSign,
  ExternalLink,
  Inbox,
  Package,
  PackageCheck,
  RefreshCw,
  Search,
  ShoppingBag,
  Undo2,
  Wifi,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { MetricCard } from '@/components/financials/MetricCard';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/Sheet';
import {
  MobileListCard,
  MobileListCardStack,
} from '@/components/ui/MobileListCard';
import { Skeleton } from '@/components/ui/Skeleton';
import { useIsMobile } from '@/lib/useMediaQuery';
import { formatCurrency, formatDate, formatRelative } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { useAuth } from '@/app/AuthProvider';
import { canWrite } from '@/lib/permissions';
import { ReturnDialog } from '@/components/returns/ReturnDialog';
import {
  useAmazonChannels,
  useSalesOrders,
  useShopifyChannels,
  useTriggerAmazonSync,
  useTriggerShopifySync,
  type OrderLineItem,
  type SalesOrderRow,
  type ShippingAddress,
} from '@/features/sales/hooks';
import type { SalesChannelKind, SalesOrderStatus } from '@/types/database';

// ---------------------------------------------------------------------------
// Sales → Orders
//
// Unified row-per-order queue across every connected channel. Today we
// ship Shopify; Amazon and the B2B portal slot into the same table once
// their syncs come online (ROADMAP §3.3). The channel filter chips are
// already wired for every kind so adding Amazon is purely a `kind` enum
// flip on the backend side.
//
// Empty-state behaviour mirrors WebAnalyticsPage: if no live orders exist
// we render a labelled sample-data preview so operators can see what the
// live version will look like, with a clear path to /integrations to
// connect their first store.
// ---------------------------------------------------------------------------

const STATUS_ORDER: SalesOrderStatus[] = [
  'draft',
  'pending_payment',
  'allocated',
  'picking',
  'packed',
  'shipped',
  'delivered',
  'on_hold',
  'cancelled',
];

const STATUS_LABELS: Record<SalesOrderStatus, string> = {
  draft: 'Draft',
  pending_payment: 'Pending payment',
  allocated: 'Allocated',
  picking: 'Picking',
  packed: 'Packed',
  shipped: 'Shipped',
  delivered: 'Delivered',
  on_hold: 'On hold',
  cancelled: 'Cancelled',
};

const STATUS_VARIANTS: Record<SalesOrderStatus, 'success' | 'info' | 'warning' | 'muted' | 'outline'> = {
  draft: 'muted',
  pending_payment: 'warning',
  allocated: 'info',
  picking: 'info',
  packed: 'info',
  shipped: 'success',
  delivered: 'success',
  on_hold: 'warning',
  cancelled: 'muted',
};

type ChannelFilter = 'all' | SalesChannelKind;

interface ChannelChip {
  id: ChannelFilter;
  label: string;
}

const CHANNEL_CHIPS: ChannelChip[] = [
  { id: 'all', label: 'All channels' },
  { id: 'shopify', label: 'Shopify' },
  { id: 'amazon', label: 'Amazon' },
  { id: 'b2b_portal', label: 'B2B portal' },
  { id: 'manual', label: 'Manual' },
];

export function OrdersPage() {
  const isMobile = useIsMobile();
  const ordersQuery = useSalesOrders();
  const shopifyChannels = useShopifyChannels();
  const amazonChannels = useAmazonChannels();
  const sync = useTriggerShopifySync();
  const amazonSync = useTriggerAmazonSync();

  // Default to Shopify because that's the channel most installs light up
  // first. The "All channels" chip stays available so operators can
  // include manual / B2B / Amazon orders without changing the default.
  const [channel, setChannel] = useState<ChannelFilter>('shopify');
  const [status, setStatus] = useState<'all' | SalesOrderStatus>('all');
  const [query, setQuery] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [returnOrder, setReturnOrder] = useState<SalesOrderRow | null>(null);
  const { role } = useAuth();
  const canManageReturns = canWrite(role, 'sales');
  const navigate = useNavigate();

  const hasShopifyChannel = (shopifyChannels.data ?? []).some((c) => c.is_active);
  const hasAmazonChannel = (amazonChannels.data ?? []).some((c) => c.is_active);
  const hasLiveOrders = (ordersQuery.data ?? []).length > 0;
  const isLive = hasShopifyChannel || hasAmazonChannel || hasLiveOrders;

  const sampleOrders = useMemo(() => generateSampleShopifyOrders(), []);
  const allOrders = useMemo<SalesOrderRow[]>(
    () => (isLive ? ordersQuery.data ?? [] : sampleOrders),
    [isLive, ordersQuery.data, sampleOrders],
  );

  const filteredOrders = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allOrders.filter((o) => {
      if (channel !== 'all' && o.channel.kind !== channel) return false;
      if (status !== 'all' && o.status !== status) return false;
      if (!q) return true;
      return (
        o.so_number.toLowerCase().includes(q) ||
        (o.customer_email ?? '').toLowerCase().includes(q) ||
        (o.customer_name ?? '').toLowerCase().includes(q) ||
        o.line_items.some(
          (li) =>
            li.name.toLowerCase().includes(q) ||
            (li.sku ?? '').toLowerCase().includes(q),
        )
      );
    });
  }, [allOrders, channel, status, query]);

  const channelCounts = useMemo(() => {
    const counts: Record<ChannelFilter, number> = {
      all: allOrders.length,
      shopify: 0,
      amazon: 0,
      b2b_portal: 0,
      manual: 0,
    };
    for (const o of allOrders) {
      if (o.channel.kind in counts) counts[o.channel.kind as ChannelFilter]++;
    }
    return counts;
  }, [allOrders]);

  const metrics = useMemo(() => computeMetrics(filteredOrders), [filteredOrders]);

  const selectedOrder =
    selectedOrderId ? filteredOrders.find((o) => o.id === selectedOrderId) ?? null : null;

  const handleSync = async () => {
    try {
      const result = await sync.mutateAsync(undefined);
      const totalIngested = result.results.reduce((s, r) => s + r.ordered, 0);
      const errored = result.results.find((r) => r.error);
      if (errored) {
        toast.error(`Sync finished with errors: ${errored.error}`);
      } else if (totalIngested > 0) {
        toast.success(`Pulled ${totalIngested} new ${totalIngested === 1 ? 'order' : 'orders'}.`);
      } else {
        toast.success('Already up to date.');
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Sync failed';
      toast.error(msg);
    }
  };

  const handleAmazonSync = async () => {
    try {
      const result = await amazonSync.mutateAsync(undefined);
      const totalIngested = result.results.reduce((s, r) => s + r.ordered, 0);
      const errored = result.results.find((r) => r.error);
      if (errored) {
        toast.error(`Amazon sync finished with errors: ${errored.error}`);
      } else if (totalIngested > 0) {
        toast.success(`Pulled ${totalIngested} new Amazon ${totalIngested === 1 ? 'order' : 'orders'}.`);
      } else {
        toast.success('Amazon already up to date.');
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Amazon sync failed';
      toast.error(msg);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Orders"
        actions={
          <div className="flex items-center gap-2">
            {hasShopifyChannel ? (
              <Badge variant="success">Shopify connected</Badge>
            ) : (
              <Badge variant="warning">Shopify not connected</Badge>
            )}
            {hasAmazonChannel ? (
              <Badge variant="success">Amazon connected</Badge>
            ) : (
              <Badge variant="warning">Amazon not connected</Badge>
            )}
            <Button
              onClick={handleSync}
              disabled={!hasShopifyChannel || sync.isPending}
              variant={hasShopifyChannel ? 'default' : 'outline'}
            >
              <RefreshCw className={cn('h-4 w-4', sync.isPending && 'animate-spin')} />
              {sync.isPending ? 'Syncing…' : 'Sync Shopify'}
            </Button>
            <Button
              onClick={handleAmazonSync}
              disabled={!hasAmazonChannel || amazonSync.isPending}
              variant={hasAmazonChannel ? 'default' : 'outline'}
            >
              <RefreshCw className={cn('h-4 w-4', amazonSync.isPending && 'animate-spin')} />
              {amazonSync.isPending ? 'Syncing…' : 'Sync Amazon'}
            </Button>
          </div>
        }
      />

      {!isLive && (
        <Card className="border-atmosphere-dark/30 bg-atmosphere/10">
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <Wifi className="mt-0.5 h-5 w-5 shrink-0 text-atmosphere-dark" strokeWidth={1.8} />
              <p className="text-sm font-medium text-foreground">Showing sample data</p>
            </div>
            <Button variant="outline" asChild>
              <Link to="/integrations" className="inline-flex items-center">
                Connect Shopify <ExternalLink className="h-4 w-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Metric strip — recomputes against the currently-filtered subset
          so flipping the channel chip immediately reflects the ROAS of
          that channel only. */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <MetricCard
          label="Orders"
          value={metrics.orderCount.toLocaleString()}
          icon={<Inbox className="h-4 w-4 text-muted-foreground" />}
          hint={isLive ? `${metrics.last24h} in the last 24 h` : 'Sample data'}
          loading={ordersQuery.isLoading && isLive}
        />
        <MetricCard
          label="Revenue"
          value={formatCurrency(metrics.totalCents)}
          icon={<DollarSign className="h-4 w-4 text-muted-foreground" />}
          hint={`Avg ${formatCurrency(metrics.avgCents)}`}
          loading={ordersQuery.isLoading && isLive}
        />
        <MetricCard
          label="Awaiting fulfilment"
          value={metrics.unfulfilled.toLocaleString()}
          icon={<Package className="h-4 w-4 text-muted-foreground" />}
          hint={`${metrics.pendingPayment} pending payment`}
          loading={ordersQuery.isLoading && isLive}
        />
        <MetricCard
          label="Shipped"
          value={metrics.shipped.toLocaleString()}
          icon={<PackageCheck className="h-4 w-4 text-muted-foreground" />}
          hint={`${metrics.delivered} delivered`}
          loading={ordersQuery.isLoading && isLive}
        />
      </div>

      {/* Channel chip row. We intentionally use Tabs as a segmented
          control without TabsContent panels — the table below is the
          single content surface and reads the active filter directly. */}
      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={channel} onValueChange={(v) => setChannel(v as ChannelFilter)}>
          <TabsList>
            {CHANNEL_CHIPS.map((c) => (
              <TabsTrigger key={c.id} value={c.id} className="gap-1.5">
                {c.label}
                <span className="rounded-full bg-secondary/80 px-1.5 py-0 text-[10px] font-medium tabular-nums text-muted-foreground">
                  {channelCounts[c.id]}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
          {CHANNEL_CHIPS.map((c) => (
            <TabsContent key={c.id} value={c.id} className="hidden" />
          ))}
        </Tabs>

        <Select value={status} onValueChange={(v) => setStatus(v as 'all' | SalesOrderStatus)}>
          <SelectTrigger className="h-9 w-44">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any status</SelectItem>
            {STATUS_ORDER.map((s) => (
              <SelectItem key={s} value={s}>
                {STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative flex-1 min-w-[16rem]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by order #, customer, SKU…"
            className="h-9 pl-9"
          />
        </div>
      </div>

      {/* Orders table — the main surface. One row per order with the
          channel tagged inline; click a row to open the detail drawer. */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <ShoppingBag className="h-4 w-4 text-muted-foreground" strokeWidth={1.7} />
            {channel === 'all' ? 'All orders' : channelLabel(channel)}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {ordersQuery.isLoading && isLive ? (
            <TableSkeleton />
          ) : filteredOrders.length === 0 ? (
            <EmptyOrders connected={hasShopifyChannel} channel={channel} />
          ) : isMobile ? (
            // Mobile: each order is a `MobileListCard` with the
            // SO number as the title, channel + status chips, the
            // total prominently on the right, and a 2-column meta
            // strip for customer + line-item summary. Tapping the
            // card opens the same detail drawer the desktop row
            // uses, so the operator gets the full order panel
            // without trying to scroll a 7-column table sideways.
            <MobileListCardStack>
              {filteredOrders.map((order) => {
                const liCount = order.line_items.reduce(
                  (s, li) => s + li.quantity,
                  0,
                );
                const headLine = order.line_items[0];
                const liSummary = headLine
                  ? order.line_items.length > 1
                    ? `${liCount} units · ${order.line_items.length} SKUs`
                    : `${headLine.quantity}× ${headLine.name}`
                  : '—';
                return (
                  <MobileListCard
                    key={order.id}
                    title={order.so_number}
                    subtitle={
                      <span>
                        {formatDate(order.ordered_at)} ·{' '}
                        {order.customer_name ||
                          order.customer_email ||
                          'No customer'}
                      </span>
                    }
                    badges={
                      <>
                        <ChannelBadge
                          kind={order.channel.kind}
                          name={order.channel.name}
                        />
                        <StatusBadge status={order.status} />
                      </>
                    }
                    amount={{
                      value: formatCurrency(order.total_cents),
                      caption: order.currency,
                    }}
                    meta={[
                      {
                        label: 'Items',
                        value: liSummary,
                      },
                      {
                        label: 'Placed',
                        value: formatRelative(order.ordered_at),
                      },
                    ]}
                    onTap={() => setSelectedOrderId(order.id)}
                  />
                );
              })}
            </MobileListCardStack>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Channel</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order) => (
                  <TableRow
                    key={order.id}
                    onClick={() => setSelectedOrderId(order.id)}
                    className="cursor-pointer"
                  >
                    <TableCell className="font-medium">
                      <div className="flex flex-col">
                        <span className="text-foreground">{order.so_number}</span>
                        {order.external_id && (
                          <span className="text-[11px] text-muted-foreground">
                            ext {order.external_id}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span>{formatDate(order.ordered_at)}</span>
                        <span className="text-[11px] text-muted-foreground">
                          {formatRelative(order.ordered_at)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <ChannelBadge kind={order.channel.kind} name={order.channel.name} />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-foreground">
                          {order.customer_name || order.customer_email || '—'}
                        </span>
                        {order.customer_name && order.customer_email && (
                          <span className="text-[11px] text-muted-foreground">
                            {order.customer_email}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <LineItemsPreview items={order.line_items} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={order.status} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-col items-end">
                        <span className="tabular-nums font-medium text-foreground">
                          {formatCurrency(order.total_cents)}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          {order.currency}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <OrderDetailDrawer
        order={selectedOrder}
        open={selectedOrder !== null}
        onOpenChange={(open) => setSelectedOrderId(open ? selectedOrderId : null)}
        onStartReturn={
          canManageReturns && isLive
            ? (order) => {
                setReturnOrder(order);
                setSelectedOrderId(null);
              }
            : undefined
        }
      />

      {canManageReturns && (
        <ReturnDialog
          open={returnOrder !== null}
          onOpenChange={(open) => !open && setReturnOrder(null)}
          initialOrder={returnOrder}
          onSaved={() => navigate('/sales/returns')}
        />
      )}
    </div>
  );
}

// ─── Small presentational helpers ────────────────────────────────────

function ChannelBadge({ kind, name }: { kind: SalesChannelKind | 'unknown'; name: string }) {
  switch (kind) {
    case 'shopify':
      return <Badge variant="success">{name || 'Shopify'}</Badge>;
    case 'amazon':
      return <Badge variant="warning">{name || 'Amazon'}</Badge>;
    case 'b2b_portal':
      return <Badge variant="info">{name || 'B2B portal'}</Badge>;
    case 'manual':
      return <Badge variant="outline">{name || 'Manual'}</Badge>;
    default:
      return <Badge variant="muted">{name || 'Unknown'}</Badge>;
  }
}

function StatusBadge({ status }: { status: SalesOrderStatus }) {
  return (
    <Badge variant={STATUS_VARIANTS[status]}>
      {STATUS_LABELS[status]}
    </Badge>
  );
}

function LineItemsPreview({ items }: { items: OrderLineItem[] }) {
  const count = items.reduce((s, li) => s + li.quantity, 0);
  if (items.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  const head = items[0];
  const more = items.length - 1;
  return (
    <div className="flex flex-col">
      <span className="truncate max-w-[16rem] text-foreground">
        {head.quantity}× {head.name}
      </span>
      <span className="text-[11px] text-muted-foreground">
        {more > 0 ? `+ ${more} more · ${count} units total` : `${count} units total`}
      </span>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-11 w-full" />
      ))}
    </div>
  );
}

function EmptyOrders({
  connected,
  channel,
}: {
  connected: boolean;
  channel: ChannelFilter;
}) {
  if (!connected && channel === 'shopify') {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
        <CircleDashed className="h-10 w-10 text-muted-foreground" strokeWidth={1.5} />
        <div className="space-y-1">
          <h3 className="text-base font-medium text-foreground">No Shopify orders yet</h3>
        </div>
        <Button asChild>
          <Link to="/integrations">Connect Shopify</Link>
        </Button>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <Inbox className="h-10 w-10 text-muted-foreground" strokeWidth={1.5} />
        <div className="space-y-1">
          <h3 className="text-base font-medium text-foreground">No orders match</h3>
        </div>
    </div>
  );
}

function OrderDetailDrawer({
  order,
  open,
  onOpenChange,
  onStartReturn,
}: {
  order: SalesOrderRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStartReturn?: (order: SalesOrderRow) => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        {order ? (
          <>
            <SheetHeader>
              <div className="flex items-center gap-2">
                <ChannelBadge kind={order.channel.kind} name={order.channel.name} />
                <StatusBadge status={order.status} />
              </div>
              <SheetTitle>{order.so_number}</SheetTitle>
              <SheetDescription>
                Placed {formatDate(order.ordered_at, 'long')} · {formatRelative(order.ordered_at)}
                {order.external_id && (
                  <> · external id <code className="rounded bg-secondary px-1">{order.external_id}</code></>
                )}
              </SheetDescription>
            </SheetHeader>
            <SheetBody className="space-y-6">
              {onStartReturn && order.status !== 'cancelled' && (
                <Button size="sm" variant="outline" onClick={() => onStartReturn(order)}>
                  <Undo2 className="h-4 w-4" />
                  Start a return
                </Button>
              )}
              <section className="space-y-2">
                <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Customer
                </h3>
                <div className="rounded-lg border border-border bg-card/40 p-3 text-sm">
                  <p className="font-medium text-foreground">
                    {order.customer_name || order.customer_email || '—'}
                  </p>
                  {order.customer_email && order.customer_name && (
                    <p className="text-muted-foreground">{order.customer_email}</p>
                  )}
                  {order.financial_status && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Payment: <span className="text-foreground">{order.financial_status}</span>
                      {order.fulfillment_status && (
                        <>
                          {' '}· Fulfilment:{' '}
                          <span className="text-foreground">{order.fulfillment_status}</span>
                        </>
                      )}
                    </p>
                  )}
                </div>
              </section>

              <section className="space-y-2">
                <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Line items ({order.line_items.length})
                </h3>
                <div className="overflow-hidden rounded-lg border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Unit</TableHead>
                        <TableHead className="text-right">Line</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {order.line_items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-muted-foreground">
                            No line items captured.
                          </TableCell>
                        </TableRow>
                      ) : (
                        order.line_items.map((li, idx) => (
                          <TableRow key={`${li.sku ?? li.name}-${idx}`}>
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="text-foreground">{li.name}</span>
                                {li.sku && (
                                  <span className="text-[11px] text-muted-foreground">
                                    SKU {li.sku}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {li.quantity}
                            </TableCell>
                            <TableCell className="text-right tabular-nums text-muted-foreground">
                              {formatCurrency(li.unit_price_cents)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums font-medium">
                              {formatCurrency(li.unit_price_cents * li.quantity)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </section>

              <section className="space-y-2">
                <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Totals
                </h3>
                <dl className="space-y-1 rounded-lg border border-border bg-card/40 p-3 text-sm">
                  <TotalRow label="Subtotal" cents={order.subtotal_cents} />
                  <TotalRow label="Shipping" cents={order.shipping_cents} />
                  <TotalRow label="Tax" cents={order.tax_cents} />
                  {order.discount_cents > 0 && (
                    <TotalRow label="Discount" cents={-order.discount_cents} />
                  )}
                  <div className="my-1 h-px bg-border/60" />
                  <div className="flex items-baseline justify-between font-medium">
                    <dt className="text-foreground">Total</dt>
                    <dd className="tabular-nums text-foreground">
                      {formatCurrency(order.total_cents)} {order.currency}
                    </dd>
                  </div>
                </dl>
              </section>

              {order.ship_to && (
                <section className="space-y-2">
                  <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Ship to
                  </h3>
                  <AddressBlock address={order.ship_to} />
                </section>
              )}

              {order.bill_to && (
                <section className="space-y-2">
                  <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Bill to
                  </h3>
                  <AddressBlock address={order.bill_to} />
                </section>
              )}
            </SheetBody>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function TotalRow({ label, cents }: { label: string; cents: number }) {
  return (
    <div className="flex items-baseline justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tabular-nums text-foreground">{formatCurrency(cents)}</dd>
    </div>
  );
}

function AddressBlock({ address }: { address: ShippingAddress }) {
  const lines = [
    address.name,
    address.address1,
    address.address2,
    [address.city, address.province_code ?? address.province, address.zip].filter(Boolean).join(', '),
    address.country,
  ].filter(Boolean);
  if (lines.length === 0) {
    return (
      <p className="rounded-lg border border-border bg-card/40 p-3 text-sm text-muted-foreground">
        No address on file.
      </p>
    );
  }
  return (
    <address className="not-italic rounded-lg border border-border bg-card/40 p-3 text-sm leading-relaxed">
      {lines.map((line, i) => (
        <div key={i} className={i === 0 ? 'font-medium text-foreground' : 'text-muted-foreground'}>
          {line}
        </div>
      ))}
    </address>
  );
}

// ─── Metric helpers ───────────────────────────────────────────────────

interface OrdersMetrics {
  orderCount: number;
  totalCents: number;
  avgCents: number;
  last24h: number;
  pendingPayment: number;
  unfulfilled: number;
  shipped: number;
  delivered: number;
}

function computeMetrics(orders: SalesOrderRow[]): OrdersMetrics {
  const day = Date.now() - 24 * 60 * 60 * 1000;
  let totalCents = 0;
  let last24h = 0;
  let pendingPayment = 0;
  let unfulfilled = 0;
  let shipped = 0;
  let delivered = 0;
  for (const o of orders) {
    totalCents += o.total_cents;
    if (new Date(o.ordered_at).getTime() >= day) last24h++;
    if (o.status === 'pending_payment') pendingPayment++;
    if (
      o.status === 'pending_payment' ||
      o.status === 'allocated' ||
      o.status === 'picking' ||
      o.status === 'packed' ||
      o.status === 'on_hold'
    ) {
      unfulfilled++;
    }
    if (o.status === 'shipped') shipped++;
    if (o.status === 'delivered') delivered++;
  }
  return {
    orderCount: orders.length,
    totalCents,
    avgCents: orders.length === 0 ? 0 : Math.round(totalCents / orders.length),
    last24h,
    pendingPayment,
    unfulfilled,
    shipped,
    delivered,
  };
}

function channelLabel(kind: ChannelFilter): string {
  switch (kind) {
    case 'all':
      return 'All orders';
    case 'shopify':
      return 'Shopify orders';
    case 'amazon':
      return 'Amazon orders';
    case 'b2b_portal':
      return 'B2B orders';
    case 'manual':
      return 'Manual orders';
    default:
      return 'Orders';
  }
}

// ─── Sample data ──────────────────────────────────────────────────────
//
// Used when no Shopify channel is connected yet — same approach as the
// WebAnalyticsPage's pre-GA4 sample series. Stable across re-renders
// because the data is generated once with a deterministic seed.

function generateSampleShopifyOrders(): SalesOrderRow[] {
  const now = Date.now();
  const base: Array<{
    daysAgo: number;
    minutes: number;
    customer: string;
    email: string;
    items: Array<{ name: string; sku: string; qty: number; price_cents: number }>;
    status: SalesOrderStatus;
    city: string;
    province: string;
  }> = [
    {
      daysAgo: 0,
      minutes: 35,
      customer: 'Avery Linwood',
      email: 'avery.linwood@example.com',
      items: [{ name: 'Daily Hydrator 50ml', sku: 'DH-50-01', qty: 1, price_cents: 3200 }],
      status: 'pending_payment',
      city: 'Austin',
      province: 'TX',
    },
    {
      daysAgo: 0,
      minutes: 110,
      customer: 'Marisol Tatum',
      email: 'marisol@example.com',
      items: [
        { name: 'Skincare Essentials Set', sku: 'SES-01', qty: 1, price_cents: 8800 },
        { name: 'Lip Repair Balm', sku: 'LRB-01', qty: 2, price_cents: 1200 },
      ],
      status: 'allocated',
      city: 'Brooklyn',
      province: 'NY',
    },
    {
      daysAgo: 0,
      minutes: 305,
      customer: 'Jonas Krieger',
      email: 'jonas.k@example.com',
      items: [{ name: 'Renewal Serum 30ml', sku: 'RS-30-01', qty: 1, price_cents: 5400 }],
      status: 'packed',
      city: 'Portland',
      province: 'OR',
    },
    {
      daysAgo: 1,
      minutes: 145,
      customer: 'Priya Venkataraman',
      email: 'priya.v@example.com',
      items: [
        { name: 'Mineral Sunscreen SPF 40', sku: 'MS-40-01', qty: 2, price_cents: 2900 },
      ],
      status: 'shipped',
      city: 'Seattle',
      province: 'WA',
    },
    {
      daysAgo: 1,
      minutes: 540,
      customer: 'Tomás Reyes',
      email: 'treyes@example.com',
      items: [
        { name: 'Bright Eye Cream', sku: 'BEC-15-01', qty: 1, price_cents: 4200 },
        { name: 'Refining Toner 120ml', sku: 'RT-120-01', qty: 1, price_cents: 2600 },
        { name: 'Cotton Rounds (60ct)', sku: 'CR-60-01', qty: 1, price_cents: 800 },
      ],
      status: 'shipped',
      city: 'Denver',
      province: 'CO',
    },
    {
      daysAgo: 2,
      minutes: 60,
      customer: 'Yumi Saito',
      email: 'yumi.saito@example.com',
      items: [{ name: 'Botanical Cleanser 150ml', sku: 'BC-150-01', qty: 1, price_cents: 2800 }],
      status: 'delivered',
      city: 'San Diego',
      province: 'CA',
    },
    {
      daysAgo: 3,
      minutes: 215,
      customer: 'Dee Whitford',
      email: 'dee.w@example.com',
      items: [{ name: 'Subscription · Hydrator', sku: 'SUB-DH-01', qty: 1, price_cents: 2880 }],
      status: 'delivered',
      city: 'Asheville',
      province: 'NC',
    },
    {
      daysAgo: 4,
      minutes: 470,
      customer: 'Ezra Goldfarb',
      email: 'ezra.g@example.com',
      items: [
        { name: 'Gift Box · Bestsellers', sku: 'GIFT-BS-01', qty: 1, price_cents: 11200 },
      ],
      status: 'delivered',
      city: 'Brooklyn',
      province: 'NY',
    },
    {
      daysAgo: 6,
      minutes: 30,
      customer: 'Wren Calloway',
      email: 'wren@example.com',
      items: [
        { name: 'Daily Hydrator 50ml', sku: 'DH-50-01', qty: 1, price_cents: 3200 },
      ],
      status: 'cancelled',
      city: 'Madison',
      province: 'WI',
    },
  ];

  return base.map((row, idx) => {
    const ordered = new Date(now - row.daysAgo * 86_400_000 - row.minutes * 60_000);
    const subtotal = row.items.reduce((s, i) => s + i.qty * i.price_cents, 0);
    const shipping = subtotal >= 7500 ? 0 : 800;
    const tax = Math.round(subtotal * 0.07);
    const total = subtotal + shipping + tax;
    return {
      id: `sample-${idx}`,
      so_number: `SH-${1000 + idx}`,
      external_id: String(5_400_000_000 + idx),
      channel: { id: null, kind: 'shopify', name: 'Shopify' },
      customer_email: row.email,
      customer_name: row.customer,
      status: row.status,
      ordered_at: ordered.toISOString(),
      subtotal_cents: subtotal,
      tax_cents: tax,
      shipping_cents: shipping,
      discount_cents: 0,
      total_cents: total,
      currency: 'USD',
      ship_to: {
        name: row.customer,
        address1: `${100 + idx * 17} Sample St`,
        city: row.city,
        province_code: row.province,
        country: 'United States',
        country_code: 'US',
        zip: String(10000 + idx * 729).slice(0, 5),
      },
      bill_to: null,
      line_items: row.items.map((it) => ({
        name: it.name,
        sku: it.sku,
        quantity: it.qty,
        unit_price_cents: it.price_cents,
      })),
      financial_status:
        row.status === 'cancelled'
          ? 'voided'
          : row.status === 'pending_payment'
            ? 'pending'
            : 'paid',
      fulfillment_status:
        row.status === 'shipped'
          ? 'fulfilled'
          : row.status === 'delivered'
            ? 'fulfilled'
            : row.status === 'cancelled'
              ? null
              : null,
    };
  });
}
